import * as amplitude from "@amplitude/analytics-browser";
import {
  ArrowFatLeft,
  ArrowFatRight,
  Clock,
  FolderPlus,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/astro";
import clsx from "clsx";
import JSZip from "jszip";
import { useEffect, useState } from "react";
import type { InstagramData } from "../lib/types";
import PrivacyLink from "./privacy-link";
import Tappable from "./tappable";

const API_HOST = "https://ig-valentines-api.elk.sh";
const LOCAL_STORAGE_EMAIL_KEY = "ig_valentines:email";

export default function Upload({
  onBack,
  onDataReady,
}: {
  onBack: () => void;
  onDataReady: (data: InstagramData) => void;
}) {
  const [reminderAvailable, setReminderAvailable] = useState(false);
  const [reminderSubmitted, setReminderSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API_HOST}/reminders/available`)
      .then((res) => res.json())
      .then((json) => {
        setReminderAvailable(json.available);
      });
  }, []);

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      const dataFile = contents.file(
        "personal_information/personal_information/personal_information.json"
      );

      if (dataFile) {
        const personal_informationString = await dataFile.async("string");
        const personal_information = JSON.parse(personal_informationString);

        const conversations: InstagramData["conversations"] = [];

        for (const filename in contents.files) {
          if (
            filename.match(
              /your_instagram_activity\/messages\/inbox\/.+\/message.*\.json/
            )
          ) {
            const conversationString = await contents.files[filename].async(
              "string"
            );

            if (conversationString.trim() === "") {
              // The parent folder /your_instagram_activity/messages/inbox also counts here
              continue;
            }

            const conversation = JSON.parse(conversationString);

            const participants = conversation.participants;

            const name = participants[0].name;

            if (name === "Meta AI") {
              continue;
            }

            if (participants.length === 2) {
              // Extract folder name from the path
              const folderName = filename.match(
                /your_instagram_activity\/messages\/inbox\/([^/]+)\/message/
              )?.[1];

              if (!folderName) continue;

              // Find if we already have a conversation with this folder
              // There can be message_1.json, message_2.json, etc.
              const existingConversation = conversations.find(
                (c) => c.folder_name === folderName
              );

              if (existingConversation) {
                // Append messages to existing conversation
                existingConversation.messages.push(...conversation.messages);
              } else {
                // Create new conversation entry
                conversations.push({
                  user_name: name,
                  folder_name: folderName,
                  messages: conversation.messages,
                });
              }
            }
          }
        }

        if (conversations.length === 0) {
          alert(
            "We couldn’t find any DM history in your Instagram data export!"
          );
          return;
        }

        const data: InstagramData = {
          personal_information,
          conversations,
        };

        const email = localStorage.getItem(LOCAL_STORAGE_EMAIL_KEY);

        if (email) {
          fetch(`${API_HOST}/reminders`, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
          });
        }

        onDataReady(data);

        if (import.meta.env.DEV) {
          console.log(data);
        }
      } else {
        alert(
          "Instagram data not found! Go back and make sure you exported your Instagram data in JSON format and selected the file that begins with “instagram-...”"
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      alert(
        "Instagram data not found! Go back and make sure you exported your Instagram data in JSON format and selected the file that begins with “instagram-...”"
      );
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setReminderSubmitted(true);
    amplitude.track("reminder_email_submitted");

    const form = event.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value
      .toLowerCase()
      .trim();

    fetch(`${API_HOST}/reminders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, email);
  };

  return (
    <div className="grid grid-rows-[max-content_max-content_1fr_max-content] h-full">
      <div className="pb-1 bg-brand-lighter-pink shadow-sm shadow-brand-dark-pink/5">
        <div className="px-2 pt-4 pb-3 border-b-2 border-brand-dark-pink/20 border-dashed">
          <p className="text-lg bg-brand-muddy-light-pink w-max text-brand-dark-pink font-bold text-center mx-auto px-4 py-0.5 rounded-full">
            Step 2
          </p>
          <p className="mt-1 text-2xl text-brand-dark-pink font-bold text-center">
            Upload Instagram profile
          </p>
        </div>
      </div>

      {reminderAvailable ? (
        <div className="mt-4 mx-6 bg-white shadow-lg rounded-xl px-4 pt-3 pb-3.5 flex gap-x-3">
          <Clock
            weight="bold"
            className="text-brand-dark-pink flex-shrink-0"
            size={20}
          />
          <div>
            <p className="text-brand-dark-pink font-medium leading-tight">
              What’s your email address?
            </p>
            <p className="mt-0.5 text-gray-400 font-medium text-sm leading-tight">
              We can send you a reminder when your Instagram file is ready.
            </p>

            <div className="relative">
              <form
                onSubmit={handleSubmit}
                className={clsx(
                  "mt-2 flex items-center gap-x-2 transition-opacity duration-100",
                  reminderSubmitted && "opacity-0 pointer-events-none"
                )}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="you@gmail.com"
                  required
                  className="w-full bg-brand-light-pink border-brand-dark-pink/50 px-2 py-0.5 rounded-md focus:ring-brand-dark-pink placeholder:text-brand-brown/30"
                />
                <Tappable>
                  <button
                    type="submit"
                    className="px-4 py-1.5 rounded-md bg-brand-dark-pink text-white h-full"
                  >
                    <ArrowFatRight size={18} weight="fill" />
                  </button>
                </Tappable>
              </form>

              <div
                className={clsx(
                  "absolute inset-0 flex items-center transition-opacity duration-100",
                  !reminderSubmitted && "opacity-0 pointer-events-none"
                )}
              >
                <p className="text-brand-dark-pink font-medium">
                  Reminder set!
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div />
      )}

      <div className="mt-4 relative bg-white rounded-4xl p-2 shadow-lg mx-6">
        <div className="border-4 border-dashed border-brand-dark-pink rounded-3xl p-4 h-full">
          <div className="h-full flex flex-col items-center justify-center">
            <FolderPlus className="size-14 text-brand-dark-pink" />
            <p className="mt-2 text-2xl font-bold text-brand-dark-pink">
              Upload File
            </p>
            <p className="mt-2 text-center text-gray-400 font-semibold">
              Tap “<Clock weight="fill" className="inline-block" /> Recents” at
              the bottom and look for “instagram-...”.
            </p>
          </div>
        </div>
        <input
          type="file"
          accept=".zip,application/zip"
          className="absolute inset-0 opacity-0"
          onChange={handleFileChange}
        />
      </div>

      <div className="mt-6 p-4 space-y-3">
        <button
          className="w-full bg-brand-muddy-light-pink rounded-xl text-brand-dark-pink flex items-center justify-center gap-x-2 py-2"
          onClick={onBack}
        >
          <ArrowFatLeft size={18} weight="fill" />
          <p className="text-lg font-semibold">Back</p>
        </button>
        <p className="text-center font-semibold text-brand-dark-pink">
          <PrivacyLink />
        </p>
      </div>
    </div>
  );
}
